<script setup lang="ts">
import { computed, unref } from 'vue';
import { isNil } from 'lodash';

import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Dropdown field type - single select from list
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  persistentPlaceholder = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  // TODO: Remove this event after the migration is complete
  input: [value: string];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  persistentPlaceholder,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const modelValueState = computed(() => (!isNil(props.modelValue) ? String(props.modelValue) : ''));

/**
 * v-autocomplete emits null when `clearable` clears the selection, and both events here are
 * declared as string. Normalizing to '' keeps the emitted type honest and matches the empty
 * value the other field types settle on.
 */
function handleInput(value: string | null) {
  const normalizedValue = value ?? '';
  emit('update:model-value', normalizedValue);
  emit('input', normalizedValue);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <v-autocomplete
      :data-testid="testId"
      clearable
      color="primary"
      density="comfortable"
      open-on-clear
      variant="outlined"
      :hide-details="normalizedProps.hideDetails"
      :items="field.dropDownValues ?? []"
      :model-value="unref(modelValueState)"
      :persistent-placeholder="normalizedProps.persistentPlaceholder"
      :placeholder="field.placeholder"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
